import React, { useMemo } from 'react'
import moment from 'moment'
import { Card, Container, Table } from 'reactstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { useOrderRedux } from '@hooks'
import { GST_STALL_LIST, processOrderStatus, ROUTES } from '@common'

const OrderReceiptPage = () => {
    const navigate = useNavigate()
    const { orderNumber } = useParams()
    const { searchedOrder } = useOrderRedux()
    const { lineitems, totalAmount } = useMemo(() => processOrderStatus(searchedOrder), [searchedOrder])

    if (!searchedOrder) {
        return (
            <Container className="py-5 text-center">
                <h3>Order not found</h3>
                <button className="btn btn-primary mt-3" onClick={() => navigate(ROUTES.Order.search)}>
                    Search Orders
                </button>
            </Container>
        )
    }

    const formatDate = dateString => {
        return moment(dateString).format('D MMM YYYY, h:mm A')
    }

    return (
        <Container className="pb-5 pt-0 receipt-container">
            <Card className="p-4 receipt-card">
                <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                        <h2 className="mb-1">Order Receipt</h2>
                        <h4 className="text-secondary mb-3">#{orderNumber}</h4>
                        <p className="mb-1">
                            <strong>Order Type:</strong> {searchedOrder.isDineIn ? 'Dine In' : 'Takeaway'}
                        </p>
                        {searchedOrder.isDineIn && (
                            <p className="mb-1">
                                <strong>Table:</strong> {searchedOrder.table.name}
                            </p>
                        )}
                        <p className="mb-1">
                            <strong>Date:</strong> {formatDate(searchedOrder.createdOn)}
                        </p>
                        <p className="mb-1">
                            <strong>Status:</strong>{' '}
                            <span className={searchedOrder.status === 'PAID' ? 'text-success' : 'text-danger'}>
                                {searchedOrder.status}
                            </span>
                        </p>
                    </div>
                    <button className="btn btn-sm btn-outline-primary no-print" onClick={() => window.print()}>
                        Print Receipt
                    </button>
                </div>

                <div className="mb-4">
                    {lineitems.map(lineitem => (
                        <Card key={lineitem.dishId} className="mb-3 print-mb">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                    <h5 className="mb-1">{lineitem.name}</h5>
                                    <p className="text-secondary mb-1">
                                        <small>{lineitem.vendorName}</small>
                                    </p>
                                </div>
                            </div>

                            <Table borderless size="sm" className="mb-2">
                                <thead>
                                    <tr className="text-secondary">
                                        <th>Item</th>
                                        <th className="text-end">Qty</th>
                                        <th className="text-end">Price</th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lineitem.items.map(({ variant, selections }) => (
                                        <React.Fragment key={variant.variantId}>
                                            <tr>
                                                <td>{variant.name}</td>
                                                <td className="text-end">{variant.quantity}x</td>
                                                <td className="text-end">${variant.sellPrice.toFixed(2)}</td>
                                                <td className="text-end">${(variant.sellPrice * variant.quantity).toFixed(2)}</td>
                                            </tr>
                                            {selections.map(selection =>
                                                selection.options.map(option => (
                                                    <tr key={option.id} className="text-secondary">
                                                        <td>
                                                            <small>+ {option.name}</small>
                                                        </td>
                                                        <td className="text-end">
                                                            <small>{option.quantity * variant.quantity}x</small>
                                                        </td>
                                                        <td className="text-end">
                                                            <small>${option.sellPrice.toFixed(2)}</small>
                                                        </td>
                                                        <td className="text-end">
                                                            <small>
                                                                $
                                                                {(option.sellPrice * option.quantity * variant.quantity).toFixed(
                                                                    2
                                                                )}
                                                            </small>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="3" className="text-end">
                                            <strong>Subtotal:</strong>
                                        </td>
                                        <td className="text-end">
                                            <strong>
                                                $
                                                {lineitem.items
                                                    .reduce((total, { variant, selections }) => {
                                                        const variantTotal = variant.sellPrice * variant.quantity
                                                        const selectionsTotal = selections.reduce(
                                                            (acc, selection) =>
                                                                acc +
                                                                selection.options.reduce(
                                                                    (optionTotal, option) =>
                                                                        optionTotal +
                                                                        option.sellPrice * option.quantity * variant.quantity,
                                                                    0
                                                                ),
                                                            0
                                                        )
                                                        return total + variantTotal + selectionsTotal
                                                    }, 0)
                                                    .toFixed(2)}
                                            </strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={4}>
                                            {GST_STALL_LIST.includes(lineitem?.stallName) && (
                                                <div className="d-flex justify-content-end">
                                                    <p className="text-secondary m-0" style={{ fontSize: '12px' }}>
                                                        (Inclusive of GST)
                                                    </p>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                </tfoot>
                            </Table>

                            {lineitem.note && (
                                <div className="mt-2">
                                    <small className="text-secondary">Note: {lineitem.note}</small>
                                </div>
                            )}
                        </Card>
                    ))}
                </div>

                <div className="border-top pt-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="mb-0">Total Amount:</h4>
                        <h4 className="mb-0">${totalAmount.toFixed(2)}</h4>
                    </div>
                </div>

                <div className="mt-4 text-center text-secondary">
                    <small>Thank you for your order!</small>
                </div>
            </Card>
        </Container>
    )
}

export default OrderReceiptPage
